import { useState, useEffect, useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import { DataGrid } from '@mui/x-data-grid'
import {
  CircularProgress,
  Tab,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import Api from '../../api'
import {
  loadStateFromLocalStorage,
  saveStateToLocalStorage,
} from '../../redux/utilsLocalStorage'
import moment from 'moment-timezone'
import {
  adjustDataWithCapacity,
  CustomToolbar,
  findGlobalMinMaxFromKeys,
  getColorGreenWhiteRed,
  getNormalizedUnit,
  mapData,
} from './QualityMonitoringSetups'
import NoSite from '../../components/NoSite'
import { PrimaryButton } from '../../components/restyled-mui/CustomButton'
import { dataKeys } from './QualityMonitoringSetups'
import SetupBreadcrumbs from './SetupBreadcrumbs'
import { connect } from 'react-redux'
import WarningIcon from '@mui/icons-material/Warning'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { calendarIcon } from '../../assets/fontawesome/calendarIcon'
import { CustomTextField } from '../../components/restyled-mui/CustomInputs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { changeStatus } from '../../redux/actions/status'
import { STATUS } from '../../redux/types'
import { useDispatch } from 'react-redux'

const columns = [
  {
    field: 'plant_name',
    headerName: 'Plants',
    flex: 1.8,
  },
  {
    field: 'threshold',
    headerName: 'Error Treshold',
    flex: 1,
  },
  {
    field: 'snooze_date',
    headerName: 'Snooze Until',
    flex: 1,
  },
  {
    field: 'latest_calibration',
    headerName: 'Last Calibration',
    flex: 1,
  },
  {
    field: 'latest_capacity',
    headerName: 'Capacity',
    flex: 0.8,
  },
  {
    field: 'd1',
    headerName: 'D-1',
    flex: 0.5,
  },
  {
    field: 'd2',
    headerName: 'D-2',
    flex: 0.5,
  },
  {
    field: 'd3',
    headerName: 'D-3',
    flex: 0.5,
  },
  {
    field: 'd7',
    headerName: 'D-7',
    flex: 0.5,
  },
  {
    field: 'd30',
    headerName: 'D-30',
    flex: 0.5,
  },
  {
    field: 'd180',
    headerName: 'D-180',
    flex: 0.5,
  },
  {
    field: 'd365',
    headerName: 'D-365',
    flex: 0.5,
  },
]

const useStyles = makeStyles(theme => ({
  mainContainer: {
    height: '95%',
    width: '100%',
    padding: '10px 20px',
  },
  tableContainer: {
    height: '80vh',
    width: '100%',
    overflowY: 'hidden',
  },
  centerItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '80vh',
    width: '100%',
  },

  alignNoData: {
    marginRight: '50px',
  },
  snoozeButton: {
    // minWidth: 0,
    px: 1.5,
    py: 0.3,
    fontWeight: 600,
    fontSize: '0.80rem',
    textTransform: 'none',
    // borderRadius: '10px', // slightly more rounded, optional
    boxShadow: 'none', // flat button
    letterSpacing: 0.2,
    lineHeight: 1.1,
  },
  tooltip: {
    color: '#C63C3C',
    backgroundColor: '#e6f0fb',
    border: '1px solid #368ED8',
  },
}))

const overridesWeirdResizing = {
  '& .MuiDataGrid-main': {
    width: 0,
    minWidth: '100%',
  },
  '& .MuiDataGrid-cell': {
    padding: 0,
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    fontSize: '0.875rem',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 'bold',
    textAlign: 'center',
  },
  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
    color: '#368ED8',
    backgroundColor: '#e3f1fb',
  },
}

const formatAndValidate = inputValue => {
  const val = String(inputValue).trim()

  const regex = /^\d{1,2}(\.\d{1,2})?$/
  if (!regex.test(val)) return { valid: false }

  const num = Number(val)
  if (isNaN(num) || num <= 0 || num >= 100) return { valid: false }

  const formatted =
    num % 1 === 0 ? num.toString() : num.toFixed(2).replace(/\.?0+$/, '')

  return { valid: true, formatted }
}

const QualityMonitoringSetupPlants = props => {
  const classes = useStyles(props)
  const {
    selectedSetup,
    history,
    mainTabValue,
    setMainTabValue,
    tabsMap,
    isActiveViewSetup,
    userPermissions,
    user,
  } = props

  const dispatch = useDispatch()
  const [qualityMonitoringPlants, setQualityMonitoringPlants] = useState([])
  const [qualityMonitoringPlantsCache, setQualityMonitoringPlantsCache] =
    useState({})
  const [
    qualityMonitoringAdjustedPlantsCache,
    setQualityMonitoringAdjustedPlantsCache,
  ] = useState({})
  const [loading, setLoading] = useState(false)
  const [nodataContent, setNodataContent] = useState({})

  const backupStoredUnit = loadStateFromLocalStorage('unit') || 'kW'
  const selectedUnit = getNormalizedUnit(user.unit || backupStoredUnit)
  const backupUserTimezone = loadStateFromLocalStorage('timezone') || 'Etc/UTC'
  const userTimezone = user.timezone || backupUserTimezone

  const [isAdjustedView, setIsAdjustedView] = useState(true)

  const [isMeteringView, setIsMeteringView] = useState(false) // false means online by default
  const [activeView, setActiveView] = useState('all')

  const filteredData = useMemo(() => {
    if (activeView === 'all') return qualityMonitoringPlants

    if (activeView === 'active') {
      return qualityMonitoringPlants.filter(item => item.latest_capacity > 0)
    }

    if (activeView === 'aboveThreshold') {
      return qualityMonitoringPlants.filter(item => item.d30 > item.threshold)
    }

    return qualityMonitoringPlants // fallback, just in case
  }, [qualityMonitoringPlants, activeView])

  const { min, max } = findGlobalMinMaxFromKeys(filteredData, dataKeys)

  const [paginationByTab, setPaginationByTab] = useState({
    1: { page: 0, pageSize: 30 },
    2: { page: 0, pageSize: 30 },
    3: { page: 0, pageSize: 30 },
  })

  const isAdmin = userPermissions.includes('settings')
  const adminOnlyFields = ['latest_calibration', 'snooze_date', 'threshold']

  // Error Treshold
  const [errorThresholds, setErrorThresholds] = useState({})
  const [activePlantID, setActivePlantID] = useState(null)

  useEffect(() => {
    if (!filteredData.length) return

    const thresholds = Object.fromEntries(
      filteredData.map(row => {
        const { valid, formatted } = formatAndValidate(String(row.threshold))

        const snoozeUntil =
          typeof row.snooze_date === 'string' && row.snooze_date.trim() !== ''
            ? row.snooze_date
            : moment().add(1, 'day').startOf('day').format('YYYY-MM-DD')

        return [
          row.plant_name,
          {
            setupID: selectedSetup.id,
            committedThreshold: valid ? Number(formatted) : '', // saved/last API value
            inputThreshold: valid ? Number(formatted) : '', // field value
            snoozeUntil,
            snoozeComment: row.comment,
          },
        ]
      }),
    )

    setErrorThresholds(thresholds)
  }, [filteredData, selectedSetup.id])

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const handleOpenDialog = plantID => {
    setActivePlantID(plantID)
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setActivePlantID(null)
  }

  const updateAPIThreshold = async (data, plantID) => {
    const { setupID, snoozeComment, snoozeUntil, threshold } = data
    const errThresholdObj = {
      setup_id: setupID,
      plant_id: plantID,
      threshold,
      comment: snoozeComment,
      snooze: snoozeUntil,
    }

    try {
      const response =
        await Api.QualityMonitoring.setQualityMonitoringPlantsThreshold(
          errThresholdObj,
        )

      if (response.status === 201) {
        dispatch(
          changeStatus(STATUS.SUCCESS, `Successfully updated plant ${plantID}`),
        )
      }
    } catch (error) {
      const msg =
        error?.response?.data?.msg ||
        error?.message ||
        `Unknown error while setting threshold parameters for ${plantID}`

      dispatch(changeStatus(STATUS.ERROR, msg))
      console.error('Error while setting threshold parameters', error)
    }
  }

  const handleChangeThreshold = plantID => e => {
    const val = e.target.value
    setErrorThresholds(prev => ({
      ...prev,
      [plantID]: {
        ...prev[plantID],
        inputThreshold: val,
      },
    }))
  }

  const handleKeyDownThreshold = plantID => e => {
    if (e.key === 'Enter') {
      e.preventDefault()

      const val = e.target.value
      const { valid, formatted } = formatAndValidate(val)

      if (!valid) {
        setErrorThresholds(prev => ({
          ...prev,
          [plantID]: {
            ...prev[plantID],
            error: true,
          },
        }))
        return
      }

      const finalValue = formatted === '' ? '' : Number(formatted)
      const prevCommitted = errorThresholds[plantID]?.committedThreshold

      if (finalValue !== prevCommitted) {
        setErrorThresholds(prev => ({
          ...prev,
          [plantID]: {
            ...prev[plantID],
            committedThreshold: finalValue, // commit
            inputThreshold: finalValue, // sync field with committed
            error: false,
          },
        }))
        updateAPIThreshold(
          { ...errorThresholds[plantID], threshold: finalValue },
          plantID,
        )
      }

      setQualityMonitoringPlants(prevRows =>
        prevRows.map(row =>
          row.plant_name === plantID ? { ...row, threshold: finalValue } : row,
        ),
      )

      // Now move focus to next row
      const currentCell = e.target.closest('[role="cell"]')
      const currentRow = currentCell?.parentElement
      const nextRow = currentRow?.nextElementSibling
      const colIndex = Array.from(currentRow?.children || []).indexOf(
        currentCell,
      )
      const nextCell = nextRow?.children?.[colIndex]
      const input = nextCell?.querySelector('input')
      setTimeout(() => input?.focus(), 0)
    }
  }

  const handleBlurThreshold = plantID => e => {
    const val = e.target.value
    const { valid } = formatAndValidate(val)

    setErrorThresholds(prev => ({
      ...prev,
      [plantID]: {
        ...prev[plantID],
        error: !valid,
      },
    }))
  }

  const handleSnoozeUpdate = () => {
    const { setupID, snoozeUntil, snoozeComment, committedThreshold } =
      errorThresholds[activePlantID]
    const apiPayload = {
      setupID,
      snoozeUntil,
      snoozeComment,
      threshold: committedThreshold,
    }

    setQualityMonitoringPlants(prev =>
      prev.map(row =>
        row.plant_name === activePlantID
          ? {
              ...row,
              snooze_date: snoozeUntil,
              comment: snoozeComment,
              threshold: committedThreshold,
            }
          : row,
      ),
    )

    // 2. API call here (optional, async)
    updateAPIThreshold(apiPayload, activePlantID)

    // 3. Close dialog
    setIsDialogOpen(false)
    setActivePlantID(null)
  }

  const colorizeData = useMemo(() => {
    const visibleColumns = isAdmin
      ? columns
      : columns.filter(col => !adminOnlyFields.includes(col.field))

    return visibleColumns.map(item => {
      if (item.field === 'latest_capacity') {
        return {
          ...item,
          headerName: selectedUnit === 'MW' ? 'Capacity (MW)' : 'Capacity (kW)',
          renderCell: params => {
            if (params.value == null) return null

            const value =
              selectedUnit === 'MW'
                ? (params.value / 1000).toFixed(2)
                : params.value

            return (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {value}
              </Box>
            )
          },
          headerAlign: 'center',
        }
      }

      if (item.field === 'latest_calibration') {
        return {
          ...item,
          renderCell: params => {
            if (params.value == null) return null
            const formatted = moment
              .unix(params.value)
              .tz(userTimezone)
              .format('YYYY-MM-DD HH:mm')

            return (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {formatted}
              </Box>
            )
          },
          headerAlign: 'center',
          headerName: 'Latest Calibration',
        }
      }

      if (item.field === 'snooze_date') {
        return {
          ...item,
          renderCell: params => {
            if (params.value == null) return '-'

            const comment = params.row.comment

            const formatted = moment(params.value, 'YYYY-MM-DD')
              .tz(userTimezone)
              .format('YYYY-MM-DD')

            return (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                }}
              >
                {formatted}
                {comment && comment.trim() !== '' && (
                  <Tooltip
                    title={comment}
                    classes={{
                      tooltip: classes.tooltip,
                    }}
                  >
                    <WarningIcon fontSize="small" style={{ color: 'red' }} />
                  </Tooltip>
                )}
              </Box>
            )
          },
          headerAlign: 'center',
          headerName: 'Snooze Until',
        }
      }

      if (item.field === 'threshold') {
        return {
          ...item,
          renderCell: params => {
            if (params.value === null) return null
            const plantID = params.row.plant_name

            const displayValue = errorThresholds[plantID]?.inputThreshold

            return (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0,
                  gap: 5,
                }}
              >
                <TextField
                  size="small"
                  value={displayValue}
                  onChange={handleChangeThreshold(plantID)}
                  onBlur={handleBlurThreshold(plantID)}
                  onKeyDown={handleKeyDownThreshold(plantID)}
                  onClick={e => e.stopPropagation()}
                  variant="outlined"
                  margin="dense"
                  fullWidth={true}
                  sx={{ maxWidth: 50 }}
                  inputProps={{
                    style: {
                      textAlign: 'center',
                      backgroundColor: errorThresholds[plantID]?.error
                        ? 'rgba(198, 60, 60, 0.30)' // #C63C3C
                        : undefined,
                    },
                    inputMode: 'decimal',
                  }}
                  error={Boolean(errorThresholds[plantID]?.error)}
                />
                <Button
                  color="primary"
                  size="small"
                  variant="contained"
                  className={classes.snoozeButton}
                  onClick={e => {
                    e.stopPropagation() // This prevents the DataGrid cell click
                    handleOpenDialog(plantID) // Open your dialog
                  }}
                >
                  Snooze
                </Button>
              </Box>
            )
          },
          headerAlign: 'center',
          headerName: 'Error Treshold',
        }
      }

      return {
        ...item,
        renderCell: params => {
          if (params.value == null) return null

          const color = getColorGreenWhiteRed(params.value, min, max)
          return (
            <Box
              sx={{
                backgroundColor: color,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {params.value}
            </Box>
          )
        },
        headerAlign: 'center',
      }
    })
  }, [columns, selectedUnit, min, max, isAdmin, errorThresholds, userTimezone])

  useEffect(() => {
    const fetchData = async () => {
      if (selectedSetup) {
        const tabKey = mainTabValue.name.toLowerCase()
        const cacheKey = `${tabKey}_${isMeteringView ? 'metering' : 'online'}`

        if (qualityMonitoringPlantsCache[cacheKey]) {
          setQualityMonitoringPlants(qualityMonitoringPlantsCache[cacheKey])
          return
        }

        setLoading(true)
        const today = new Date()
        const qualitySetupObj = {
          setup: selectedSetup.name,
          period: tabKey,
          date: moment(today).format('YYYY-MM-DD'),
          type: isMeteringView ? 'metering' : 'online',
        }
        try {
          const response =
            await Api.QualityMonitoring.getQualityMonitoringSetupPlants(
              qualitySetupObj,
            )

          if (response.status === 200) {
            const formattedData = mapData(response.data, false)
            setQualityMonitoringPlants(formattedData)

            // Cache it
            setQualityMonitoringPlantsCache(prev => ({
              ...prev,
              [cacheKey]: formattedData,
            }))
          }
          setNodataContent({
            heading: `The data is empty for selected setup: ${
              selectedSetup.name
            } (${isMeteringView ? 'Metering' : 'Online'} view)`,
            body: '',
          })

          setLoading(false)
        } catch (error) {
          setQualityMonitoringPlants(null)
          setNodataContent({
            heading: `No data found for selected setup: ${
              selectedSetup.name
            } (${isMeteringView ? 'Metering' : 'Online'} view)`,
            body: 'Try switching between Metering and Online views to see if data is available in the other view.',
          })
          setLoading(false)
        }
      }
    }

    fetchData()
  }, [selectedSetup, mainTabValue, isMeteringView])

  const handleMainTabChange = (_, newValue) => {
    const selectedTab = tabsMap.find(tab => tab.id === newValue)
    setMainTabValue(selectedTab)
    history.push(`/monitoring/client/${selectedSetup.name}`)
  }

  const handleCellClick = params => {
    if (
      params.field === 'plant_name' ||
      params.field === 'latest_capacity' ||
      params.field === 'latest_calibration' ||
      params.field === 'threshold' ||
      params.field === 'snooze_date'
    )
      return

    const plantData = {
      setupName: selectedSetup.name,
      plantName: params.row.plant_name,
      forecastType:
        mainTabValue.name === 'composite_1h' ? 'Composite' : mainTabValue.name,
      plantCellValue: params.value,
      dateRange: params.field,
    }

    // Check if any value in plantData is null or undefined
    const hasNull = Object.values(plantData).some(
      value => value === null || value === undefined,
    )
    if (hasNull) return

    saveStateToLocalStorage(plantData, 'quality2MetricsData')
    const url = `${window.location.origin}/metrics?fromHandler=true`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleToggleErrorView = nextIsAdjustedView => {
    const tabKey = mainTabValue.name.toLowerCase()
    const cacheKey = `${tabKey}_${isMeteringView ? 'metering' : 'online'}`

    // optional safety (usually redundant if UI guards newValue !== null)
    if (nextIsAdjustedView === isAdjustedView) return

    if (nextIsAdjustedView) {
      // Switching TO Percentage / Initial
      if (qualityMonitoringPlantsCache[cacheKey]) {
        setQualityMonitoringPlants(qualityMonitoringPlantsCache[cacheKey])
      }
    } else {
      // Switching TO Capacity Adjusted
      if (qualityMonitoringAdjustedPlantsCache[cacheKey]) {
        setQualityMonitoringPlants(
          qualityMonitoringAdjustedPlantsCache[cacheKey],
        )
      } else {
        // adjust from BASE, not from currently displayed data
        const base =
          qualityMonitoringPlantsCache[cacheKey] ?? qualityMonitoringPlants

        const adjusted = adjustDataWithCapacity(
          base,
          selectedUnit,
          'latest_capacity',
        )

        setQualityMonitoringPlants(adjusted)
        setQualityMonitoringAdjustedPlantsCache(prev => ({
          ...prev,
          [cacheKey]: adjusted,
        }))
      }
    }

    setIsAdjustedView(nextIsAdjustedView)
  }

  const handlePaginationChange = model => {
    setPaginationByTab(prev => ({
      ...prev,
      [mainTabValue.id]: model,
    }))
  }

  const handleMeteringToggle = isMetering => {
    setIsMeteringView(isMetering)
    // We don't need to clear the cache here since we're using the combined cache key
    // that includes both tab and type, so switching types will automatically
    // trigger a new API call if the data isn't cached
  }

  const handleActiveToggle = newValue => {
    setActiveView(newValue)
  }

  const renderDataGrid = () => {
    const currentPagination = paginationByTab[mainTabValue.id] || {
      page: 0,
      pageSize: 30,
    }

    return (
      <div className={classes.tableContainer}>
        <DataGrid
          rows={filteredData}
          rowHeight={30}
          columns={colorizeData}
          getRowId={row => row.plant_name}
          disableColumnSelector
          disableColumnMenu
          filterMode="client"
          paginationModel={currentPagination}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={[30]}
          autoPageSize={false}
          slots={{
            toolbar: props => (
              <CustomToolbar
                {...props}
                tableName={mainTabValue.name}
                isAdjustedView={isAdjustedView}
                handleToggleErrorView={handleToggleErrorView}
                isMeteringView={isMeteringView}
                handleMeteringToggle={handleMeteringToggle}
                activeView={activeView}
                handleActiveToggle={handleActiveToggle}
                placeholderName={'Plant'}
                isAdmin={isAdmin}
              />
            ),
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true, // Enables search bar
              quickFilterProps: { debounceMs: 300 },
            },
          }}
          sx={overridesWeirdResizing}
          onCellClick={handleCellClick}
          localeText={{
            toolbarExportPrint: 'Print as PDF',
            toolbarExport: 'Export',
          }}
        />
      </div>
    )
  }

  return (
    <div className={classes.mainContainer}>
      <SetupBreadcrumbs
        history={history}
        activeView={isActiveViewSetup}
        setupId={selectedSetup?.name}
      />
      <TabContext value={mainTabValue.id}>
        <Box sx={{ borderBottom: 0 }}>
          <TabList
            onChange={handleMainTabChange}
            aria-label="Quality monitoring"
            sx={{
              // indicator + selected color
              '& .MuiTabs-indicator': { backgroundColor: '#368ED8' },
              '& .MuiTab-root.Mui-selected': { color: '#368ED8' },
              '& .MuiTab-root': {
                minWidth: '160px',
              },
            }}
          >
            <Tab label="ID" value="1" />
            <Tab label="DAH" value="2" />
            {/* <Tab
              
              label="Composite"
              value="3"
            /> */}
          </TabList>
        </Box>

        {/* Content: Show Spinner While Loading */}
        {loading ? (
          <div className={classes.centerItem}>
            <CircularProgress />
          </div>
        ) : qualityMonitoringPlants.length !== 0 ? (
          <>
            <TabPanel value="1" sx={{ p: '30px 0' }}>
              <div>{renderDataGrid()}</div>
            </TabPanel>

            <TabPanel value="2" sx={{ p: '30px 0' }}>
              <div>{renderDataGrid()}</div>
            </TabPanel>

            <TabPanel value="3" sx={{ p: '30px 0' }}>
              <div>{renderDataGrid()}</div>
            </TabPanel>
          </>
        ) : (
          <div className={classes.centerItem}>
            <div className={classes.alignNoData}>
              <NoSite heading={nodataContent.heading} body={nodataContent.body}>
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'center',
                  }}
                >
                  <PrimaryButton
                    onClick={() => setIsMeteringView(!isMeteringView)}
                    style={{ minWidth: '200px' }}
                  >
                    Switch to {isMeteringView ? 'Online' : 'Metering'} View
                  </PrimaryButton>
                  <PrimaryButton
                    onClick={() => history.push(`/monitoring/client`)}
                    style={{ minWidth: '200px' }}
                  >
                    Back to Client
                  </PrimaryButton>
                </div>
              </NoSite>
            </div>
          </div>
        )}
      </TabContext>

      {/** Rendering dialog outside of the DataGrid */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Snooze Settings</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" py={3}>
            <div style={{ marginBottom: 15 }}>
              <DatePicker
                minDate={moment().add(1, 'day').startOf('day').toDate()}
                label="Snooze until"
                value={
                  activePlantID && errorThresholds[activePlantID]?.snoozeUntil
                    ? moment(
                        errorThresholds[activePlantID].snoozeUntil,
                        'YYYY-MM-DD',
                      ).toDate()
                    : null
                }
                PopperProps={{
                  placement: 'bottom-start',
                }}
                onChange={date => {
                  setErrorThresholds(prev => ({
                    ...prev,
                    [activePlantID]: {
                      ...prev[activePlantID],
                      snoozeUntil: date
                        ? moment(date).format('YYYY-MM-DD')
                        : null,
                    },
                  }))
                }}
                inputFormat="YYYY-MM-DD"
                components={{
                  OpenPickerIcon: () => (
                    <FontAwesomeIcon
                      icon={calendarIcon}
                      className={classes.endAdornmentIcon}
                    />
                  ),
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    sx={{ mb: 4 }}
                    InputProps={{
                      ...params.InputProps,
                      className: classes.calendarField,
                    }}
                  />
                )}
              />
            </div>

            <CustomTextField
              sx={{ mt: 4 }}
              label="Reason / Explanation"
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
              type="text"
              placeholder="Please explain why this error should be snoozed..."
              value={
                activePlantID
                  ? errorThresholds[activePlantID]?.snoozeComment
                  : ''
              }
              onChange={e => {
                setErrorThresholds(prev => ({
                  ...prev,
                  [activePlantID]: {
                    ...prev[activePlantID],
                    snoozeComment: e.target.value,
                  },
                }))
              }}
              // error={Boolean(formState.aliasError)}
              // helperText={formState.aliasError}
              // disabled={!creating} // add temporary disable for existing setups in order to not edit
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ pr: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSnoozeUpdate}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = state => ({
  userPermissions: state.userPermissions.permissions,
  user: state.user,
})

export default connect(mapStateToProps)(QualityMonitoringSetupPlants)
